<template>
  <div class="image-gallery">
    <div v-if="images.length === 0" class="no-images">
      <i class="fas fa-image"></i>
      <p>No images available</p>
    </div>
    <div v-else class="gallery-container">
      <div class="main-image">
        <img 
          :src="currentImage.image_url" 
          :alt="currentImage.caption || 'Cruise image'"
          @error="handleImageError"
        />
        <div v-if="currentImage.caption" class="image-caption">
          {{ currentImage.caption }}
        </div>
      </div>
      <div v-if="images.length > 1" class="thumbnail-container">
        <button 
          v-for="(image, index) in images" 
          :key="image.id"
          @click="selectImage(index)"
          :class="['thumbnail', { active: index === currentIndex }]"
        >
          <img 
            :src="image.thumbnail_url || image.image_url" 
            :alt="image.caption || `Image ${index + 1}`"
            @error="handleThumbnailError"
          />
        </button>
      </div>
      <div v-if="images.length > 1" class="gallery-controls">
        <button @click="previousImage" class="control-btn" :disabled="currentIndex === 0">
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="image-counter">{{ currentIndex + 1 }} / {{ images.length }}</span>
        <button @click="nextImage" class="control-btn" :disabled="currentIndex === images.length - 1">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'ImageGallery',
  props: {
    images: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const currentIndex = ref(0);
    
    const currentImage = computed(() => {
      if (props.images.length === 0) return null;
      return props.images[currentIndex.value] || props.images[0];
    });
    
    const selectImage = (index) => {
      if (index >= 0 && index < props.images.length) {
        currentIndex.value = index;
      }
    };
    
    const nextImage = () => {
      if (currentIndex.value < props.images.length - 1) {
        currentIndex.value++;
      } else {
        currentIndex.value = 0; // Loop to first
      }
    };
    
    const previousImage = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
      } else {
        currentIndex.value = props.images.length - 1; // Loop to last
      }
    };
    
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
    };
    
    const handleThumbnailError = (event) => {
      event.target.src = 'https://via.placeholder.com/100x100?text=Image';
    };
    
    // Reset to first image when images change
    watch(() => props.images, () => {
      currentIndex.value = 0;
    });
    
    return {
      currentIndex,
      currentImage,
      selectImage,
      nextImage,
      previousImage,
      handleImageError,
      handleThumbnailError
    };
  }
};
</script>

<style scoped>
.image-gallery {
  width: 100%;
}

.no-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--cs-d-muted);
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
}

.no-images i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.gallery-container {
  position: relative;
}

.main-image {
  position: relative;
  width: 100%;
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--cs-d-surface-alt);
}

.main-image img {
  width: 100%;
  height: auto;
  display: block;
  max-height: 500px;
  object-fit: cover;
}

.image-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: white;
  padding: 16px;
  font-size: 14px;
}

.thumbnail-container {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
  margin-bottom: 16px;
}

.thumbnail {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background-color: var(--cs-d-surface-alt);
  padding: 0;
  transition: border-color 0.2s;
}

.thumbnail:hover {
  border-color: var(--cs-d-link);
}

.thumbnail.active {
  border-color: var(--cs-d-link);
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.control-btn {
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.control-btn:hover:not(:disabled) {
  background-color: #1557b0;
}

.control-btn:disabled {
  background-color: #dadce0;
  cursor: not-allowed;
}

.image-counter {
  font-size: 14px;
  color: var(--cs-d-muted);
  font-weight: 500;
}

@media (max-width: 768px) {
  .main-image img {
    max-height: 300px;
  }
  
  .thumbnail {
    width: 60px;
    height: 60px;
  }
}
</style>

